import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

// Layout (eager — needed for every authed page)
import DashboardLayout from './components/DashboardLayout';
import ScrollToTop from './components/ScrollToTop';
import VersionChecker from './components/VersionChecker';
import { useMaintenanceStatus } from './hooks/useMaintenanceStatus';

/* ─────────────────────────────────────────────────────────────────────────
   Code-splitting: each route is loaded on demand so customers don't ship
   admin chunks (and vice versa). Cuts initial JS payload dramatically.
   ───────────────────────────────────────────────────────────────────────── */
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PickupManDashboard = lazy(() => import('./pages/PickupManDashboard'));
const PickupManPickupDetail = lazy(() => import('./pages/PickupManPickupDetail'));
const PickupManMapView = lazy(() => import('./pages/PickupManMapView'));
const Orders = lazy(() => import('./pages/Orders'));
const AddInternationalOrder = lazy(() => import('./pages/AddInternationalOrder'));
const SelectCourier = lazy(() => import('./pages/SelectCourier'));
const ViewOrder = lazy(() => import('./pages/ViewOrder'));
const ViewPickup = lazy(() => import('./pages/ViewPickup'));
const EditInternationalOrder = lazy(() => import('./pages/EditInternationalOrder'));
const Pickups = lazy(() => import('./pages/Pickups'));
const Manifests = lazy(() => import('./pages/Manifests'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Settings = lazy(() => import('./pages/Settings'));
const RateTable = lazy(() => import('./pages/RateTable'));
const Support = lazy(() => import('./pages/Support'));
const Tracking = lazy(() => import('./pages/Tracking'));
const Terms = lazy(() => import('./pages/Terms'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Refunds = lazy(() => import('./pages/Refunds'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const Invoice = lazy(() => import('./pages/Invoice'));
const MerchantAgreement = lazy(() => import('./pages/MerchantAgreement'));
const QnA = lazy(() => import('./pages/QnA'));
const QuestionDetail = lazy(() => import('./pages/QuestionDetail'));
const RateManagement = lazy(() => import('./pages/RateManagement'));
const AdminWalletManagement = lazy(() => import('./pages/AdminWalletManagement'));
const AdminUserManagement = lazy(() => import('./pages/AdminUserManagement'));
const KYCPage = lazy(() => import('./pages/KYCPageRefactored'));
const AdminKYC = lazy(() => import('./pages/AdminKYC'));
const AdminOrdersUnified = lazy(() => import('./pages/AdminOrdersUnified'));
const AdminOrderManagement = lazy(() => import('./pages/AdminOrderManagement'));
const AdminPickupDetail = lazy(() => import('./pages/AdminPickupDetail'));
const AdminQnA = lazy(() => import('./pages/AdminQnA'));
const AdminPerformance = lazy(() => import('./pages/AdminPerformance'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminInvoices = lazy(() => import('./pages/AdminInvoices'));
const Integrations = lazy(() => import('./pages/Integrations'));
const OrderInbox = lazy(() => import('./pages/OrderInbox'));
const Products = lazy(() => import('./pages/Products'));
const CityCourier = lazy(() => import('./pages/CityCourier'));
const AdminProductManagement = lazy(() => import('./pages/AdminProductManagement'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AmazonAuthorize = lazy(() => import('./pages/AmazonAuthorize'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));

const FullPageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { status: maintenance } = useMaintenanceStatus();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/" replace />;
  // Admins are never blocked. Customers/Pickup Men see lockout page when their role is active.
  if (user.role === 'customer' && maintenance?.customer?.active) {
    return <MaintenancePage info={maintenance.customer} role="customer" />;
  }
  if (user.role === 'pickup_man' && maintenance?.pickup_man?.active) {
    return <MaintenancePage info={maintenance.pickup_man} role="pickup_man" />;
  }
  return children;
};

// Admin Only Route
const AdminRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

// Pickup Man Only Route
const PickupManRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { status: maintenance } = useMaintenanceStatus();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'pickup_man') return <Navigate to="/dashboard" replace />;
  if (maintenance?.pickup_man?.active) {
    return <MaintenancePage info={maintenance.pickup_man} role="pickup_man" />;
  }
  return children;
};

// Public Route (redirect to appropriate dashboard based on role)
const PublicRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { status: maintenance } = useMaintenanceStatus();
  if (isLoading) return <FullPageSpinner />;
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'pickup_man') {
      if (maintenance?.pickup_man?.active) {
        return <MaintenancePage info={maintenance.pickup_man} role="pickup_man" />;
      }
      return <Navigate to="/pickupman" replace />;
    }
    if (maintenance?.customer?.active) {
      return <MaintenancePage info={maintenance.customer} role="customer" />;
    }
    return <Navigate to="/customer" replace />;
  }
  return children;
};

// Smart Dashboard Redirect - redirects to appropriate dashboard based on role
const SmartDashboardRedirect = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'pickup_man') return <Navigate to="/pickupman" replace />;
  return <Navigate to="/customer" replace />;
};

// Resumes a pending Amazon Appstore authorization once the seller logs in.
const AmazonAuthorizeResume = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (user) {
      const pending = localStorage.getItem('pending_amazon_state');
      if (pending) {
        localStorage.removeItem('pending_amazon_state');
        navigate(`/amazon/authorize?state=${pending}`, { replace: true });
      }
    }
  }, [user, navigate]);
  return null;
};

function AppRoutes() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <AmazonAuthorizeResume />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/amazon/authorize" element={<AmazonAuthorize />} />
        <Route path="/tracking" element={<Tracking />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/international-courier/:city" element={<CityCourier />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/merchant-agreement" element={<MerchantAgreement />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Smart redirect for /dashboard based on role */}
        <Route path="/dashboard" element={<SmartDashboardRedirect />} />

        {/* Admin Dashboard */}
        <Route path="/admin" element={<AdminRoute><DashboardLayout><AdminDashboard /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/rate-management" element={<AdminRoute><DashboardLayout><RateManagement /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/orders" element={<AdminRoute><DashboardLayout><AdminOrdersUnified /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/wallet" element={<AdminRoute><DashboardLayout><AdminWalletManagement /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><DashboardLayout><AdminUserManagement /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/kyc" element={<AdminRoute><DashboardLayout><AdminKYC /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/pickup-management" element={<AdminRoute><DashboardLayout><AdminOrdersUnified /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/order-management" element={<AdminRoute><DashboardLayout><AdminOrderManagement /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/pickup/:pickupId" element={<AdminRoute><DashboardLayout><AdminPickupDetail /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/qna" element={<AdminRoute><DashboardLayout><AdminQnA /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/performance" element={<AdminRoute><DashboardLayout><AdminPerformance /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><DashboardLayout><AdminAnalytics /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/invoices" element={<AdminRoute><DashboardLayout><AdminInvoices /></DashboardLayout></AdminRoute>} />
        <Route path="/admin/products" element={<AdminRoute><DashboardLayout><AdminProductManagement /></DashboardLayout></AdminRoute>} />

        {/* Pickup Man Dashboard */}
        <Route path="/pickupman" element={<PickupManRoute><DashboardLayout><PickupManDashboard /></DashboardLayout></PickupManRoute>} />
        <Route path="/pickupman/map" element={<PickupManRoute><DashboardLayout><PickupManMapView /></DashboardLayout></PickupManRoute>} />
        <Route path="/pickupman/pickup/:pickupId" element={<PickupManRoute><DashboardLayout><PickupManPickupDetail /></DashboardLayout></PickupManRoute>} />

        {/* Customer Dashboard (default for customers) */}
        <Route path="/customer" element={<ProtectedRoute><DashboardLayout><Dashboard /></DashboardLayout></ProtectedRoute>} />

        {/* Protected Routes with Dashboard Layout (accessible by all logged-in users) */}
        <Route path="/orders" element={<ProtectedRoute><DashboardLayout><Orders /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/add" element={<ProtectedRoute><DashboardLayout><AddInternationalOrder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/add/international" element={<ProtectedRoute><DashboardLayout><AddInternationalOrder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/edit/:id" element={<ProtectedRoute><DashboardLayout><EditInternationalOrder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/international/:id" element={<ProtectedRoute><DashboardLayout><ViewOrder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/select-courier" element={<ProtectedRoute><DashboardLayout><SelectCourier /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/pickup/:pickupId" element={<ProtectedRoute><DashboardLayout><ViewPickup /></DashboardLayout></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><DashboardLayout><ViewOrder /></DashboardLayout></ProtectedRoute>} />
        <Route path="/pickups" element={<ProtectedRoute><DashboardLayout><Pickups /></DashboardLayout></ProtectedRoute>} />
        <Route path="/manifests" element={<ProtectedRoute><DashboardLayout><Manifests /></DashboardLayout></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><DashboardLayout><Wallet /></DashboardLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/settings/*" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/rates" element={<ProtectedRoute><DashboardLayout><RateTable /></DashboardLayout></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><DashboardLayout><Support /></DashboardLayout></ProtectedRoute>} />
        <Route path="/kyc" element={<ProtectedRoute><DashboardLayout><KYCPage /></DashboardLayout></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><DashboardLayout><Invoice /></DashboardLayout></ProtectedRoute>} />
        <Route path="/qna" element={<ProtectedRoute><DashboardLayout><QnA /></DashboardLayout></ProtectedRoute>} />
        <Route path="/qna/:id" element={<ProtectedRoute><DashboardLayout><QuestionDetail /></DashboardLayout></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><DashboardLayout><Integrations /></DashboardLayout></ProtectedRoute>} />
        <Route path="/integrations/inbox" element={<ProtectedRoute><DashboardLayout><OrderInbox /></DashboardLayout></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute><DashboardLayout><Products /></DashboardLayout></ProtectedRoute>} />

        {/* Catch all - real 404 page (noindex) instead of silently rendering the
            homepage at a wrong URL, which Google reads as duplicate content. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <VersionChecker />
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
